import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Newspaper, Lightbulb, Heart, Sparkles, Search, Loader2, AlertCircle, FileText, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { MagazineHeroArticle } from "@/components/magazine/MagazineHeroArticle";
import { MagazineLargeCard } from "@/components/magazine/MagazineLargeCard";
import { MagazineStandardCard } from "@/components/magazine/MagazineStandardCard";
import { MagazineCompactCard } from "@/components/magazine/MagazineCompactCard";
import { PullQuote } from "@/components/magazine/PullQuote";
import { EditorialCallout } from "@/components/magazine/EditorialCallout";
import { EditorLetter } from "@/components/magazine/EditorLetter";

type ArticleCategory = "trending" | "expert" | "stories" | "tips" | "news" | "all";

interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  readTime: number;
  image: string;
  isFeatured?: boolean;
  isHot?: boolean;
  slug: string;
}

const AllTheGoss = () => {
  const [selectedCategory, setSelectedCategory] = useState<ArticleCategory>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const categories = [
    { id: "all", label: "All the Goss", icon: Newspaper },
    { id: "trending", label: "Hot Takes", icon: Flame },
    { id: "expert", label: "Expert Columns", icon: Lightbulb },
    { id: "stories", label: "Real Stories", icon: Heart },
    { id: "tips", label: "Wellness Tips", icon: Sparkles },
    { id: "news", label: "News", icon: FileText },
  ];

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('articles')
        .select('*')
        .lte('published_at', new Date().toISOString())
        .order('published_at', { ascending: false });

      if (fetchError) throw fetchError;

      const formattedArticles: Article[] = (data || []).map(article => ({
        id: article.id,
        title: article.title,
        excerpt: article.excerpt || article.content.substring(0, 150) + '...',
        category: article.category.toLowerCase(),
        author: article.author_name,
        readTime: article.read_time || 5,
        image: article.featured_image_url || "/lovable-uploads/8a5edc06-3aa5-42e6-9ce8-4742f7e2dddb.png",
        isFeatured: article.is_featured || false,
        isHot: false, // Can be enhanced later with trending logic
        slug: article.slug
      }));

      setArticles(formattedArticles);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles. Please try again later.');
      toast({
        title: "Error",
        description: "Failed to load articles. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter((article) => {
    const matchesCategory = selectedCategory === "all" || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredArticle = articles.find(a => a.isFeatured);
  const regularArticles = filteredArticles.filter(a => !a.isFeatured);

  return (
    <div className="min-h-screen bg-background">
      {/* Magazine Header */}
      <div className="border-b border-border/40 bg-background sticky top-0 z-50 backdrop-blur-sm bg-background/95">
        <div className="container mx-auto px-4 py-8">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors mb-4 inline-block">
            ← Back to Home
          </Link>
          
          <div className="text-center mb-6">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-2">
              All the Goss
            </h1>
            <p className="text-sm md:text-base text-muted-foreground uppercase tracking-widest">
              Essential Reading on Menopause
            </p>
          </div>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-base"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading articles...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert variant="destructive" className="mb-8">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error}
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-4"
                onClick={fetchArticles}
              >
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!loading && !error && (
          <>
        {/* Letter from the Editor */}
        <EditorLetter 
          editorName="Sarah Mitchell"
          editorTitle="Editor-in-Chief"
          editorImage="/editor-photo.jpg"
          message="Welcome to the very first edition of All the Goss. For too long, menopause has been whispered about, minimized, or dismissed altogether. We're here to change that. This column is dedicated to every woman who deserves honest, evidence-based information about what's happening to her body—without judgment, without shame, and certainly without being told it's 'all in your head.' Here, you'll find expert insights, real stories from our community, practical wellness strategies, and the latest health news that actually matters. Whether you're navigating perimenopause, in the thick of menopause, or supporting someone who is, this is your space. Let's talk about it all—the good, the challenging, and everything in between. Because you deserve to know the goss."
        />

        {/* Category Navigation */}
        <div className="border-y border-border/40 py-4 mb-12">
          <div className="container mx-auto px-4">
            <div className="flex gap-6 overflow-x-auto scrollbar-hide justify-center">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id as ArticleCategory)}
                    className={`whitespace-nowrap text-sm uppercase tracking-wide transition-colors ${
                      isActive
                        ? "text-foreground font-semibold border-b-2 border-foreground pb-1"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-3 w-3 inline mr-1.5" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 pb-16">
          {/* Hero Article */}
          {featuredArticle && selectedCategory === "all" && !searchQuery && (
            <div className="mb-16 animate-fade-in">
              <MagazineHeroArticle
                title={featuredArticle.title}
                excerpt={featuredArticle.excerpt}
                author={featuredArticle.author}
                readTime={featuredArticle.readTime}
                image={featuredArticle.image}
                category={featuredArticle.category}
                slug={featuredArticle.slug}
              />
            </div>
          )}

          {/* Magazine Grid Layout */}
          {regularArticles.length > 0 && (
            <div className="space-y-16">
              {/* First section: 2 large cards */}
              {regularArticles.slice(0, 2).length > 0 && (
                <div className="grid md:grid-cols-2 gap-8">
                  {regularArticles.slice(0, 2).map((article) => (
                    <MagazineLargeCard
                      key={article.id}
                      title={article.title}
                      excerpt={article.excerpt}
                      author={article.author}
                      readTime={article.readTime}
                      image={article.image}
                      category={article.category}
                      slug={article.slug}
                    />
                  ))}
                </div>
              )}

              {/* Pull Quote */}
              {regularArticles.length > 2 && (
                <PullQuote 
                  text="Understanding your body's changes is the first step to taking control of your menopause journey"
                  author="The Editors"
                />
              )}

              {/* Second section: 3 standard cards */}
              {regularArticles.slice(2, 5).length > 0 && (
                <div className="grid md:grid-cols-3 gap-8">
                  {regularArticles.slice(2, 5).map((article) => (
                    <MagazineStandardCard
                      key={article.id}
                      title={article.title}
                      excerpt={article.excerpt}
                      author={article.author}
                      readTime={article.readTime}
                      image={article.image}
                      category={article.category}
                      slug={article.slug}
                    />
                  ))}
                </div>
              )}

              {/* Editorial Callout */}
              {regularArticles.slice(5).length > 0 && (
                <EditorialCallout 
                  title="Join Our Community"
                  description="Connect with thousands of women navigating menopause together. Share experiences, get support, and discover what works for you."
                />
              )}

              {/* Third section: Compact list */}
              {regularArticles.slice(5).length > 0 && (
                <>
                  <div className="flex items-center gap-4 my-12">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-sm uppercase tracking-wide text-muted-foreground">More Stories</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {regularArticles.slice(5).map((article) => (
                      <MagazineCompactCard
                        key={article.id}
                        title={article.title}
                        author={article.author}
                        readTime={article.readTime}
                        image={article.image}
                        category={article.category}
                        slug={article.slug}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

        {/* No Results */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-20">
            <div className="max-w-md mx-auto">
              <Newspaper className="h-16 w-16 text-muted-foreground/50 mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-3">No Articles Found</h3>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                We couldn't find any articles matching your search. Try adjusting your filters or exploring different categories.
              </p>
              <Button 
                size="lg"
                onClick={() => { 
                  setSearchQuery(""); 
                  setSelectedCategory("all"); 
                }}
              >
                View All Articles
              </Button>
            </div>
          </div>
        )}

          {/* Newsletter CTA */}
          <div className="mt-20 border-t border-b border-border/40 py-12 text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-3">
              Never Miss the Goss
            </h3>
            <p className="text-muted-foreground mb-8 text-lg">
              Essential reading, delivered weekly
            </p>
            <div className="flex gap-3 max-w-md mx-auto">
              <Input 
                placeholder="Your email address" 
                type="email"
                className="h-12"
              />
              <Button size="lg" className="px-8">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AllTheGoss;
